/**
 * Static SCORM 1.2 assets that ship inside an exported package, plus the
 * manifest builder. These are plain ES5 scripts written out verbatim into the
 * zip (no bundling) — they run in the LMS frame alongside the inlined runtime.
 *
 * Division of labour:
 *   - scorm-api.js          finds the LMS API (walking parent/opener frames)
 *                           and exposes a tiny BW_SCORM facade.
 *   - branchworks-adapter.js wires the runtime's event/exit hooks to the LMS:
 *                           incomplete on init, completed on session_completed,
 *                           finish on exit. Completion-only (no score).
 *   - imsmanifest.xml        a single-SCO SCORM 1.2 manifest.
 *
 * The API finder and manifest shape follow the long-established SCORM 1.2
 * conventions also used by SVEN.
 */

/** SCORM 1.2 API discovery + a minimal facade. Classic script, runs first. */
export const SCORM_API_JS = `(function () {
  function findAPI(win) {
    var tries = 0;
    while (win && tries < 25) {
      if (win.API) return win.API;
      if (win.parent && win.parent !== win) { win = win.parent; tries++; } else break;
    }
    return null;
  }
  function getAPI() {
    var api = findAPI(window);
    if (!api && window.opener) api = findAPI(window.opener);
    return api;
  }
  var API = getAPI();
  var initialized = false, finished = false;
  function bool(v) { return (v + '') === 'true'; }
  window.BW_SCORM = {
    available: function () { return !!API; },
    init: function () {
      if (!API || initialized) return initialized;
      initialized = bool(API.LMSInitialize(''));
      return initialized;
    },
    get: function (k) { return API ? API.LMSGetValue(k) : ''; },
    set: function (k, v) { return API ? bool(API.LMSSetValue(k, v)) : false; },
    commit: function () { return API ? bool(API.LMSCommit('')) : false; },
    finish: function () {
      if (!API || finished) return false;
      finished = true;
      return bool(API.LMSFinish(''));
    }
  };
})();`;

/** Completion-only adapter. Classic script, runs after the API + scenario. */
export const ADAPTER_JS = `(function () {
  var S = window.BW_SCORM;
  if (!S || !S.available()) {
    // No LMS in scope (e.g. opening the package outside an LMS). The scenario
    // still plays; we simply can't report progress.
    return;
  }
  S.init();
  var status = S.get('cmi.core.lesson_status');
  if (!status || status === 'not attempted' || status === 'unknown') {
    S.set('cmi.core.lesson_status', 'incomplete');
    S.commit();
  }

  var startMs = Date.now();
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function sessionTime() {
    var t = Math.max(0, Math.floor((Date.now() - startMs) / 1000));
    return pad(Math.floor(t / 3600)) + ':' + pad(Math.floor((t % 3600) / 60)) + ':' + pad(t % 60);
  }

  var completed = false;
  function complete() {
    if (completed) return;
    completed = true;
    S.set('cmi.core.lesson_status', 'completed');
    S.set('cmi.core.session_time', sessionTime());
    S.commit();
  }

  window.__BRANCHWORKS_ON_EVENT__ = function (event) {
    if (event && event.type === 'session_completed') complete();
  };

  window.__BRANCHWORKS_ON_EXIT__ = function () {
    S.set('cmi.core.session_time', sessionTime());
    S.commit();
    S.finish();
  };

  // Safety net: report time and finish if the learner closes without Exit.
  window.addEventListener('beforeunload', function () {
    try { S.set('cmi.core.session_time', sessionTime()); S.commit(); S.finish(); } catch (e) {}
  });
})();`;

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** Build a single-SCO SCORM 1.2 manifest listing every packaged file. */
export function buildManifest(opts: { title: string; id: string; files: string[] }): string {
  const title = escapeXml(opts.title || 'Branching scenario');
  const id = opts.id;
  const fileEls = opts.files.map((f) => `      <file href="${escapeXml(f)}"/>`).join('\n');
  return `<?xml version="1.0" encoding="UTF-8"?>
<manifest identifier="${id}-manifest" version="1.2"
  xmlns="http://www.imsproject.org/xsd/imscp_rootv1p1p2"
  xmlns:adlcp="http://www.adlnet.org/xsd/adlcp_rootv1p2"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.imsproject.org/xsd/imscp_rootv1p1p2 imscp_rootv1p1p2.xsd http://www.adlnet.org/xsd/adlcp_rootv1p2 adlcp_rootv1p2.xsd">
  <metadata>
    <schema>ADL SCORM</schema>
    <schemaversion>1.2</schemaversion>
  </metadata>
  <organizations default="${id}-org">
    <organization identifier="${id}-org">
      <title>${title}</title>
      <item identifier="${id}-item" identifierref="${id}-res">
        <title>${title}</title>
      </item>
    </organization>
  </organizations>
  <resources>
    <resource identifier="${id}-res" type="webcontent" adlcp:scormtype="sco" href="index.html">
${fileEls}
    </resource>
  </resources>
</manifest>
`;
}
